import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, PlusCircle, Trash2, Users as UsersIcon, KeyRound } from 'lucide-react';
import { getAllUsers, createUser, deleteUser, getCurrentUser, resetUserPassword } from '../utils/api';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setUsers(await getAllUsers());
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await createUser(formData);
      setFormData({ username: '', password: '', email: '', role: 'user' });
      setShowForm(false);
      fetchUsers();
      alert('User created successfully! They can now log in from any computer with the username and password you set.');
    } catch (err) {
      console.error('createUser failed:', err);
      alert(err.message || 'Failed to create user. Please try again.');
    }
  };

  const handleDelete = async (userId) => {
    const user = users.find(u => u.id === userId);
    
    // Prevent deleting the main admin
    if (user.username === 'admin') {
      alert('Cannot delete the main admin user!');
      return;
    }

    if (window.confirm(`Are you sure you want to delete user: ${user.username}?`)) {
      try {
        await deleteUser(userId);
        fetchUsers();
      } catch (err) {
        alert(err.message || 'Failed to delete user.');
      }
    }
  };

  const handleReset = async (user) => {
    const pw = window.prompt(`Set a new password for "${user.username}" (min 8 characters):`);
    if (pw === null) return;
    if (pw.length < 8) { alert('Password must be at least 8 characters.'); return; }
    try {
      await resetUserPassword(user.id, pw);
      alert(`Password updated for ${user.username}. Share the new password with them securely.`);
    } catch (err) {
      alert(err.message || 'Failed to reset password.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <UsersIcon size={32} />
              User Management
            </h1>
          </div>
          {getCurrentUser()?.role === 'admin' && (
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <PlusCircle size={20} />
              {showForm ? 'Cancel' : 'Add User'}
            </Button>
          )}
        </div>

        {/* Add User Form */}
        {showForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create New User</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="username">Username *</Label>
                    <input
                      type="text"
                      id="username"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="johndoe"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <input
                      type="password"
                      id="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Min 6 characters"
                      minLength="6"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="role">Role *</Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full mt-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Create User
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>All Users ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border-2 border-slate-200 rounded-lg hover:border-blue-300 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-slate-900 text-lg">{user.username}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user.role}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-600">Email:</span>
                        <span className="ml-2 font-semibold text-slate-900">{user.email}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Created:</span>
                        <span className="ml-2 font-semibold text-slate-900">
                          {new Date(user.created_at).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    {getCurrentUser()?.role === 'admin' && (
                      <Button
                        onClick={() => handleReset(user)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`reset-password-${user.username}`}
                      >
                        <KeyRound size={16} className="mr-1" /> Reset Password
                      </Button>
                    )}
                    {getCurrentUser()?.role === 'admin' && user.username !== 'admin' && (
                      <Button
                        onClick={() => handleDelete(user.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                    {user.username === 'admin' && (
                      <span className="text-xs text-slate-500 italic">Main Admin</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserManagement;
